import type { RangePickerProps } from 'antd/es/date-picker';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { toRange } from './rangePickerAntd';
import { candidateRange, type PartialRangeValue, type RangeEndpoint, type RangeValue } from './rangePickerState';

type CellProps = Pick<RangePickerProps, 'cellRender' | 'minDate' | 'maxDate' | 'disabledDate'>;

export interface CandidateCellsInput {
  /** Pair the picker currently shows. */
  readonly current: PartialRangeValue | null;
  /** Endpoint a hovered cell would be written to. */
  readonly active: RangeEndpoint;
  /** Closing unmounts the cells before mouseleave fires, so the hover resets on this flag. */
  readonly open: boolean;
  readonly props: CellProps;
}

/** Horizontal part of a day cell in half-cell units: [0, 2] is the whole cell, [1, 2] its end half. */
type CellSpan = readonly [number, number];

const CANDIDATE = 'xm-range-picker-cell-candidate';
const DROPPED = 'xm-range-picker-cell-dropped';

const cx = (...parts: (string | false | null | undefined)[]): string => parts.filter(Boolean).join(' ');

const inRange = (day: Dayjs, range: RangeValue): boolean => !day.isBefore(range[0], 'day') && !day.isAfter(range[1], 'day');

// Same geometry as antd's band: endpoints keep their inner half, a one-day range keeps nothing.
function spanIn(day: Dayjs, range: RangeValue | null): CellSpan | null {
  if (!range || !inRange(day, range)) return null;
  const span: CellSpan = [day.isSame(range[0], 'day') ? 1 : 0, day.isSame(range[1], 'day') ? 1 : 2];
  return span[0] < span[1] ? span : null;
}

function overlap(left: CellSpan, right: CellSpan | null): CellSpan | null {
  if (!right) return null;
  const from = Math.max(left[0], right[0]);
  const to = Math.min(left[1], right[1]);
  return to > from ? [from, to] : null;
}

// Half-cell spans leave at most one piece outside the band.
function outside(span: CellSpan, band: CellSpan | null): CellSpan | null {
  const cut = overlap(span, band);
  if (!cut) return span;
  if (cut[0] === span[0] && cut[1] === span[1]) return null;
  return cut[0] === span[0] ? [cut[1], span[1]] : [span[0], cut[0]];
}

const spanClasses = (base: string, span: CellSpan | null): string | false =>
  !!span && cx(base, span[0] === 1 && `${base}-start`, span[1] === 1 && `${base}-end`);

function candidateClasses(day: Dayjs, range: RangeValue | null, selected: RangeValue | null): string {
  if (!range) return '';
  const next = spanIn(day, range);
  const band = spanIn(day, selected);
  return cx(
    next && spanClasses(CANDIDATE, outside(next, band)),
    band && spanClasses(DROPPED, outside(band, next)),
  );
}

function isCellDisabled(day: Dayjs, constraints: CellProps): boolean {
  if (constraints.minDate && day.isBefore(constraints.minDate, 'day')) return true;
  if (constraints.maxDate && day.isAfter(constraints.maxDate, 'day')) return true;
  return !!constraints.disabledDate?.(day, { type: 'date' });
}

/**
 * Paints the hovered cell as a candidate range from the anchored endpoint, over antd's own band.
 * @param input shown pair, anchored endpoint, open flag and the consumer props of the current render
 */
export function useCandidateCells({ current, active, open, props }: CandidateCellsInput): RangePickerProps['cellRender'] {
  const [hover, setHover] = useState<Dayjs | null>(null);
  useEffect(() => { if (!open) setHover(null); }, [open]);
  const candidate = hover && !isCellDisabled(hover, props) ? candidateRange(current, active, hover) : null;
  const selected = toRange(current);
  return (cell, info) => {
    const origin = props.cellRender ? props.cellRender(cell, info) : info.originNode;
    if (info.type !== 'date' || !dayjs.isDayjs(cell)) return origin;
    const preview = isCellDisabled(cell, props) ? null : candidate;
    return (
      <div className={cx('xm-range-picker-cell', candidateClasses(cell, preview, selected))} onMouseEnter={() => setHover(cell)} onMouseLeave={() => setHover(null)}>
        {origin}
      </div>
    );
  };
}
